import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import { Text } from '@/hen-ui/atomics/Text';
import { Bar } from '@/hen-ui/atomics/Bar';
import {
  statStylesVariants,
  type StatStylesVariantProps,
} from './stat-styles.variants';

export interface BarItem {
  /** Label displayed inside the bar */
  label: string;
  /** Numeric value used for proportional sizing */
  value: number;
  /** Color variant of the bar */
  variant?: 'primary' | 'secondary' | 'tertiary';
}

export type StatStylesValueColor = 'default' | 'success' | 'error';

const valueColorClass: Record<StatStylesValueColor, string | undefined> = {
  default: undefined,
  success: 'text-success-500',
  error: 'text-error-500',
};

export interface StatStylesProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style'>,
    StatStylesVariantProps {
  /** Primary display value (big-number mode) */
  value?: string | number;
  /**
   * Semantic color applied to the value text in big-number mode.
   * Maps to HEN token classes internally — callers never reference raw
   * Tailwind utilities.
   * - `'success'` — green (`text-success-500`)
   * - `'error'`   — red (`text-error-500`)
   * - `'default'` / omitted — inherits the surrounding text color
   * @default 'default'
   */
  valueColor?: StatStylesValueColor;
  /** Subtext displayed below the value (big-number mode) */
  subtext?: string;
  /** Array of bar data (bars-vertical and bars-horizontal modes) */
  bars?: BarItem[];
  /** Maximum value for proportional bar sizing. Defaults to the largest bar value. */
  max?: number;
}

export const StatStyles = React.forwardRef<HTMLDivElement, StatStylesProps>(
  (
    {
      className,
      style: styleProp = 'big-number',
      value,
      valueColor = 'default',
      subtext,
      bars,
      max,
      ...props
    },
    ref,
  ) => {
    const resolvedMax =
      max ?? (bars ? Math.max(...bars.map((b) => b.value)) : 0);

    const getBarPercent = (barValue: number) =>
      resolvedMax > 0 ? (barValue / resolvedMax) * 100 : 0;

    const renderBars = (
      orientation: 'horizontal' | 'vertical',
      barClassName?: string,
    ) =>
      bars?.map((bar, i) => (
        <Bar
          key={i}
          label={bar.label}
          variant={bar.variant ?? 'primary'}
          orientation={orientation}
          value={getBarPercent(bar.value)}
          className={barClassName}
        />
      ));

    return (
      <div
        ref={ref}
        className={cn(statStylesVariants({ style: styleProp }), className)}
        {...props}
      >
        {styleProp === 'big-number' && (
          <>
            {value !== undefined && (
              <Text
                variant='d2'
                tag='span'
                className={cn('font-bold', valueColorClass[valueColor])}
              >
                {value}
              </Text>
            )}
            {subtext && (
              <Text variant='b5' color='subtext'>
                {subtext}
              </Text>
            )}
          </>
        )}
        {styleProp === 'bars-vertical' && renderBars('vertical', 'flex-1')}
        {styleProp === 'bars-horizontal' && renderBars('horizontal')}
      </div>
    );
  },
);

StatStyles.displayName = 'StatStyles';
